import { Calendar, User, BookOpen, RotateCcw, CheckSquare, AlertCircle, Circle, DollarSign } from "lucide-react";

export default function LoanCard({ loan, returned, onReturn, onRenew }) {
  const today = new Date().toISOString().slice(0, 10);
  
  // Computed Status Configuration Object (Dark Theme Specs)
  let statusConfig = {
    borderColor: "border-l-emerald-500",
    badgeBg: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
    statusText: "Active",
    statusIcon: <Circle className="h-2 w-2 fill-current" />
  };

  if (returned) {
    statusConfig = {
      borderColor: "border-l-stone-600",
      badgeBg: "bg-stone-800 text-stone-400 border-stone-700",
      statusText: "Returned",
      statusIcon: <CheckSquare className="h-3 w-3" />
    };
  } else if (loan.due_date < today) {
    statusConfig = {
      borderColor: "border-l-rose-500",
      badgeBg: "bg-rose-500/10 text-rose-400 border-rose-500/20",
      statusText: "Overdue",
      statusIcon: <AlertCircle className="h-3 w-3" />
    };
  } else if (loan.due_date === today) {
    statusConfig = {
      borderColor: "border-l-amber-500",
      badgeBg: "bg-amber-500/10 text-amber-400 border-amber-500/20",
      statusText: "Due Today",
      statusIcon: <AlertCircle className="h-3 w-3" />
    };
  }

  // Fallback structural safety cleanups
  const bookTitle = loan.book?.title || loan.book_title || loan.title || "Untitled Document";
  const patronDisplay = loan.patron_name || `${loan.patron?.surname || ""}, ${loan.patron?.firstname || ""}`.trim() || "Unknown Patron";
  const cardNumber = loan.patron?.cardnumber || loan.cardnumber || "N/A";

  return (
    <div
      className={`p-4 bg-[#1a1613] border-y border-r border-[#2e2720] font-dynamic-title border-l-4 rounded-2xl shadow-md transition-all flex flex-col sm:flex-row sm:items-center justify-between gap-4 ${
        statusConfig.borderColor
      } ${returned ? "opacity-60" : ""}`}
    >
      {/* Left Area: Metadata Matrix */}
      <div className="space-y-2 flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          {/* Status Badge */}
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-lg text-[11px] font-bold border ${statusConfig.badgeBg}`}>
            {statusConfig.statusIcon}
            {statusConfig.statusText}
          </span>
          
          {/* Barcode / Call Number Anchor */}
          {loan.book?.barcode && (
            <span className="text-[10px] font-mono font-bold bg-[#131110] border border-[#2e2720] text-stone-400 px-2 py-0.5 rounded-md">
              {loan.book.barcode}
            </span>
          )}
        </div>

        {/* Book Title Header */}
        <h4 className="text-sm font-bold text-stone-100 tracking-tight truncate max-w-2xl flex items-center gap-2">
          <BookOpen className="h-4 w-4 text-amber-500/70 shrink-0" />
          <span className="truncate font-serif tracking-widest">{bookTitle}</span>
        </h4>

        {/* Patron & Date Info Row */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-stone-400">
          <span className="flex items-center gap-1.5 min-w-0">
            <User className="h-3.5 w-3.5 text-stone-500 shrink-0" />
            <span className="truncate font-sans font-semibold text-stone-300">{patronDisplay}</span>
            <span className="text-stone-500 font-mono text-[11px]">({cardNumber})</span>
          </span>

          <span className="hidden sm:inline text-stone-600">•</span>

          <span className="flex items-center gap-1.5 shrink-0">
            <Calendar className="h-3.5 w-3.5 text-stone-500 shrink-0" />
            <span>Maturity Date: <strong className="text-stone-300 font-mono">{loan.due_date}</strong></span>
          </span>
        </div>
      </div>

      {/* Right Area: Monetary Fines & Action Operations */}
      <div className="flex items-center justify-between sm:justify-end gap-3 shrink-0 border-t sm:border-t-0 pt-3 sm:pt-0 border-[#2e2720]">
        {/* Dynamic Accrued Fine Block */}
        {loan.fine > 0 ? (
          <div className="text-right flex items-center gap-1.5 bg-rose-500/10 text-rose-400 px-3 py-1.5 rounded-xl border border-rose-500/20">
            <DollarSign className="h-3.5 w-3.5 text-rose-400 shrink-0" />
            <div className="text-left sm:text-right">
              <p className="text-[9px] font-bold uppercase tracking-wider text-rose-400/80 leading-none">Accrued Fine</p>
              <p className="text-xs font-bold font-mono mt-0.5 leading-none text-rose-300">
                {Number(loan.fine).toLocaleString()} <span className="text-[10px] font-sans font-semibold">Riels</span>
              </p>
            </div>
          </div>
        ) : (
          <div className="hidden md:block w-1" />
        )}

        {/* Action Controls */}
        <div className="flex items-center gap-2 ml-auto sm:ml-0">
          {!returned && (
            <>
              <button
                type="button"
                onClick={onRenew}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold text-stone-300 bg-[#131110] border border-[#2e2720] rounded-xl hover:text-amber-400 hover:border-amber-500/30 transition shadow-xs"
              >
                <RotateCcw className="h-3.5 w-3.5 text-stone-400" />
                <span>Renew</span>
              </button>
              
              <button
                type="button"
                onClick={onReturn}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-bold text-stone-950 bg-emerald-500 hover:bg-emerald-400 rounded-xl shadow transition"
              >
                <CheckSquare className="h-3.5 w-3.5 stroke-[2.5]" />
                <span>Return</span>
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}